export const randomString = (int: number): string => Array.from(Array(int), () => Math.floor(Math.random() * 36).toString(36)).join('');
export const combineClassNames = (...args: (string | undefined)[]) => [...args].join(' ').trim()
export const pluck = (objs, property) => objs.map((obj) => obj[property]);
export const findObjWithType = (obj: any, type: string) => obj?.find(o => o.type === type);
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
export const formatLastFour = (number: string) => '(...' + number?.slice(-4) + ')';
export const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric'  });
export const dollarFormat = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
export const stringifyCache = (cache) => JSON.stringify(Array.from(cache.keys()).reduce((obj: any, key) => ({ ...obj, [String(key)]: cache.get(key) }), {}));
export const mapStringCache = (stringCache) => new Map(Object.entries(JSON.parse(stringCache)));
export const makeOptions = (name, length): string[] => Array.from({ length: length }, (_, i) => `${name}.${i + 1}`)

export const formatDateInput = (value: string): string => {
  if (!value) return '';
  
  // If already formatted correctly (MM/DD/YYYY or MM/DD/YY), preserve it
  const alreadyFormatted = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(value);
  if (alreadyFormatted) return value;
  
  // Otherwise, clean and format
  const cleaned = value.replace(/\D/g, '');
  if (!cleaned) return '';
  
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
};

// Validate MM/DD/YY or MM/DD/YYYY date format. YY is for pre-existing dates that may have been entered with 2-digit year. We will convert to 4-digit year on the backend using the same logic as validation (YY < 50 => 2000s, YY >= 50 => 1900s).

export const validateDateFormat = (value: string): boolean => {
  if (!value) return true;
  
  const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{2}|\d{4})$/;
  if (!dateRegex.test(value)) return false;
  
  const [month, day, yearStr] = value.split('/');
  const monthNum = parseInt(month);
  const dayNum = parseInt(day);
  let year = parseInt(yearStr);
  
  if (yearStr.length === 2) {
    year = year < 50 ? 2000 + year : 1900 + year;
  }
  
  if (monthNum < 1 || monthNum > 12) return false;
  
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) {
    daysInMonth[1] = 29; // Leap year
  }
  if (dayNum < 1 || dayNum > daysInMonth[monthNum - 1]) return false;
  
  if (year < 1900) return false;
  
  return true;
};

/**
 * Validates that date includes month/day/year (e.g. rejects 12/20)
 */
export const validateDateComplete = (value: string): boolean => {
  if (!value) return true;

  const parts = value.split('/');
  if (parts.length !== 3) return false;

  const [month, day, year] = parts;
  if (!month || !day || !year) return false;

  const isMonthValid = /^\d{1,2}$/.test(month);
  const isDayValid = /^\d{1,2}$/.test(day);
  const isYearValid = /^\d{2}(\d{2})?$/.test(year);

  return isMonthValid && isDayValid && isYearValid;
};

/**
 * Validates that a date is not today or in the future (must be in the past)
 */
export const validateNotFuture = (value: string): boolean => {
  if (!value) return true;
  
  const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{2}|\d{4})$/;
  if (!dateRegex.test(value)) return false;
  
  const [month, day, yearStr] = value.split('/');
  let year = parseInt(yearStr);
  
  if (yearStr.length === 2) {
    year = year < 50 ? 2000 + year : 1900 + year;
  }
  
  const dateObj = new Date(year, parseInt(month) - 1, parseInt(day));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return dateObj.getTime() < today.getTime();
};

/**
The formatToISODate function converts from the form display format (MM/DD/YYYY) to the SpiceRoad API format (YYYY-MM-DD)
 */
export const formatToISODate = (dateStr: string): string => {
  if (!dateStr) return '';

  // Validate input format before splitting
  const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{2}|\d{4})$/;
  if (!dateRegex.test(dateStr)) {
    return '';
  }
  
  const [month, day, yearPart] = dateStr.split('/');
  let year = parseInt(yearPart);

  if (yearPart.length === 2) {
    year = year < 50 ? 2000 + year : 1900 + year;
  }

  // Ensure zero-padding for month and day
  const paddedMonth = month.padStart(2, '0');
  const paddedDay = day.padStart(2, '0');

  return `${year}-${paddedMonth}-${paddedDay}`;
};

/**
 * Converts a date from YYYY-MM-DD (API format) to MM/DD/YYYY (form display format).
 * Also handles MM/DD/YYYY input (returns as-is) for defensive programming.
 */
export const formatFromISODate = (dateStr: string): string => {
  if (!dateStr) return '';
  
  // Check if already in MM/DD/YYYY format
  const mmddyyyyMatch = dateStr.match(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{4})$/);
  if (mmddyyyyMatch) {
    return dateStr; // Already in display format, return as-is
  }
  
  // Try ISO format YYYY-MM-DD
  const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return '';
  
  const [, year, month, day] = dateMatch;
  const result = `${month}/${day}/${year}`;
  
  // Validate the converted date is actually valid
  if (!validateDateFormat(result)) {
    console.error(`formatFromISODate: Invalid date after conversion '${dateStr}' -> '${result}'`);
    return '';
  }
  
  return result;
};
